<script setup>

import { computed, inject, ref } from "vue";
import { useI18n } from "vue-i18n";
import { DateTime, Settings } from 'luxon';
import { saveAs } from 'file-saver';
import { useCommunitiesStore } from "@/stores/communitiesStore";
import { useCommunityCompaniesStore } from "@/stores/communityCompaniesStore";
import { extractDateRangesFromDateArray, formatDate, formatDateTime } from "../lib/es-bill-shared-js/misc/timeTools";
import { useExportStore } from "@/stores/exportStore";

const $log = inject('log');
const { t } = useI18n();
Settings.defaultZone = 'Europe/Vienna'; // might be redundant

const communitiesStore = useCommunitiesStore();
const communityCompaniesStore = useCommunityCompaniesStore();
const exportStore = useExportStore();


const communityAvailable = computed(() => {
  const communities = communitiesStore.allCommunities;

  return communities.map(c => {
    return {
      title: c.name,
      props: { subtitle: c.ecid },
      ecn: c.ecn,
      ecid: c.ecid,
      community: c,
      communityCompany: communityCompaniesStore.allCommunityCompanies.find(cc => cc.ecn === c.ecn),
    };
  })
})

const selection = ref({
  communityEntry: null,
  dateRange: [],
});

const loadingFromBackend = ref(false)

const requestParameters = computed(() => {
  let dr = [];
  if(selection.value.dateRange.length === 1) {
    const dateStart = DateTime.fromISO(selection.value.dateRange[0]);
    $log.warn(dateStart)
    dr = [dateStart.toJSDate(), dateStart.toJSDate()];
  }
  else if(selection.value.dateRange.length > 1) {
    const dateStart = DateTime.fromISO(selection.value.dateRange[0]);
    let dateEnd = DateTime.fromISO(selection.value.dateRange[
      selection.value.dateRange.length-1]);

    dateEnd=dateEnd.set({ hour: 0, minute: 0, second: 0, millisecond: 0 })
    dr = [dateStart.toJSDate(), dateEnd.toJSDate()]
  }

  return {
    community: selection.value.communityEntry?.community || null,
    dateRange: dr,
  }
})


async function performExport() {
  $log.debug("performExport start");
  loadingFromBackend.value = true;

  try {
    const csvString = await exportStore.getEnergyDumpForCommunity(
      requestParameters.value.community.ecn,
      requestParameters.value.community.ecid,
      requestParameters.value.dateRange[0],
      requestParameters.value.dateRange[1],
    );

    const fileNameParts = [
      requestParameters.value.community.ecn,
      requestParameters.value.community.ecid,
      "Energiesummen",
      formatDate(requestParameters.value.dateRange[0]),
      "bis",
      formatDate(requestParameters.value.dateRange[1]),
      "vom",
      formatDate(new Date()),
    ];

    const downloadData = new Blob([csvString], { type: "text/csv;charset=utf-8" });
    saveAs(downloadData,fileNameParts.join('_')+".csv");
  } catch (e) {
    $log.error("performExport exception", e);
  }

  loadingFromBackend.value = false
  $log.debug("performExport exit");
}

</script>
<script>
export default {
  name: 'ViewExportEnergyDump',
}
</script>


<template>
  <v-container class="containerBase pa-0">

    <v-container class="containerStandard">

      <p class="textHeadline">{{ $t('energyDump') }}</p>
      Alle Energiedaten, um Meta-Daten angereichert (aktuelle Zuweisung)<br/>
      <br/>

      <v-combobox
                  max-width="228px"
                  bg-color="white"
                  :class="{'mr-0': $vuetify.display.xs}"
                  :placeholder="t('community')"
                  v-model="selection.communityEntry"
                  :items="communityAvailable"
                  :filter-keys="['title', 'value']"
                  density="comfortable"
                  variant="outlined"
                  item-value="ecid"
                  rounded="2"
                  hide-details
                  clearable
      ></v-combobox>
      <v-date-picker v-model="selection.dateRange"
                     show-adjacent-months
                     hide-header
                     width="350" multiple='range'>
      </v-date-picker>

      Gemeinschaft: {{ requestParameters.community?.name}}<br/>
      ECID: {{ requestParameters.community?.ecid}}<br/>
      DateRange: {{ formatDate(requestParameters.dateRange[0]) }} bis inkl.
        {{ formatDate(requestParameters.dateRange[1]) }}<br/>
      <br/>


      <PrimaryButton
        :disabled="loadingFromBackend"
        @click="performExport"
        :text="$t('performExport')"
      />
      <br/><br/>

      <span v-if="loadingFromBackend">Ich bin ein Ladekringel</span>

    </v-container>
  </v-container>
</template>

<style>


</style>
